import { useEffect, useState } from 'react';
import { Heading, Text, Card, Button, Spinner } from 'frosted-ui';
import { api } from '../../services/api';
import type { ExperienceAttachResult, Placement, VisitActivity } from '../../types';
import { spotName } from '../../utils/paths';
import { pluralize } from '../../utils/variants';
import CopyButton from '../CopyButton';
import SpotActions, { type SpotManagement } from './SpotActions';
import { CheckRow } from './TestSummaryAside';
import { AddAppInstructions } from './StepPlaceInWhop';

const POLL_MS = 10_000;

interface FirstVisitChecklistProps {
  experimentId: string;
  placements: Placement[];
  /** Offers Rename and Remove on each spot (the results page). */
  manage?: SpotManagement;
  freeProductName?: string;
  attach?: ExperienceAttachResult | null;
}

/**
 * in_whop: has the app reached the free product? Starts from what the
 * wizard's attach found, then polls (lookup only) until Whop shows it, or
 * until the first load of the experience records it.
 */
function useInWhopExperience(
  experimentId: string,
  placement: Placement | undefined,
  attach: ExperienceAttachResult | null | undefined
): string | null {
  const initial =
    placement?.experienceId ??
    (attach && 'experienceId' in attach ? attach.experienceId : null);
  const [experienceId, setExperienceId] = useState<string | null>(initial);
  const placementId = placement?.id;

  useEffect(() => {
    if (experienceId || !placementId) return;
    let cancelled = false;
    const check = async () => {
      try {
        const { attach: result } = await api.linkPlacementExperience(experimentId, placementId, false);
        if (!cancelled && 'experienceId' in result) setExperienceId(result.experienceId);
      } catch (err) {
        console.error('Failed to check the free product for the app:', err);
      }
    };
    check();
    const handle = setInterval(check, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [experimentId, placementId, experienceId]);

  return experienceId;
}

/** Copyable list of a test's links; partners get a "Copy all". */
export function PlacementLinks({
  placements,
  manage,
}: {
  placements: Placement[];
  manage?: SpotManagement;
}) {
  const links = placements.filter((p): p is Placement & { link: string } => Boolean(p.link));
  // A spot inside a whop has no link, but with Remove on offer it's listed too.
  const inWhop = manage ? placements.filter((p) => p.kind === 'in_whop') : [];
  if (links.length === 0 && inWhop.length === 0) return null;
  const all = links.map((p) => `${spotName(p)}: ${p.link}`).join('\n');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {inWhop.map((p) => (
        <div
          key={p.id}
          style={{
            padding: 'var(--space-3)',
            borderRadius: 'var(--radius-3)',
            backgroundColor: 'var(--gray-a3)',
          }}
        >
          <Text size="2" weight="medium" style={{ display: 'block' }}>
            Inside your whop
          </Text>
          <Text size="2" color="gray" style={{ display: 'block', overflowWrap: 'anywhere' }}>
            Upgrade tab in {p.label}
          </Text>
          {manage && <SpotActions placement={p} manage={manage} />}
        </div>
      ))}
      {links.map((p) => (
        <div
          key={p.id}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 'var(--space-2) var(--space-3)',
            padding: 'var(--space-3)',
            borderRadius: 'var(--radius-3)',
            backgroundColor: 'var(--gray-a3)',
          }}
        >
          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
            <Text size="2" weight="medium" style={{ display: 'block', overflowWrap: 'anywhere' }}>
              {spotName(p)}
              {p.affiliateCode ? ` · ${p.affiliateCode}` : ''}
            </Text>
            <Text
              size="2"
              color="gray"
              style={{ display: 'block', fontFamily: 'var(--code-font-family, monospace)', wordBreak: 'break-all' }}
            >
              {p.link}
            </Text>
          </div>
          <CopyButton text={p.link} />
          {p.affiliateRejected && (
            <Text size="2" color="red" role="alert" style={{ flexBasis: '100%' }}>
              Whop didn't accept the username “{p.affiliateCode}”, so {spotName(p)} gets no credit for
              sales.{manage ? ' Fix it under Rename.' : ''}
            </Text>
          )}
          {manage && (
            <div style={{ flexBasis: '100%' }}>
              <SpotActions placement={p} manage={manage} />
            </div>
          )}
        </div>
      ))}
      {links.length > 1 && (
        <div>
          <CopyButton text={all} label="Copy all links" variant="soft" />
        </div>
      )}
    </div>
  );
}

// "I've posted it" is the seller's own note to themselves: per browser is fine.
function readPosted(experimentId: string): boolean {
  try {
    return localStorage.getItem(`pl-posted-${experimentId}`) === '1';
  } catch {
    return false;
  }
}

function savePosted(experimentId: string): void {
  try {
    localStorage.setItem(`pl-posted-${experimentId}`, '1');
  } catch {
    // Private window or blocked storage: the tick just won't survive a reload.
  }
}

/**
 * "Waiting for first visit": ticks on real traffic, not on one visit: the
 * seller's own click on their link is anonymous, so one visitor might be them.
 * Polls the activity endpoint.
 */
export default function FirstVisitChecklist({
  experimentId,
  placements,
  manage,
  freeProductName: freeProductNameProp,
  attach,
}: FirstVisitChecklistProps) {
  const [activity, setActivity] = useState<VisitActivity | null>(null);
  const [posted, setPosted] = useState(() => readPosted(experimentId));
  const arrived = activity?.realTraffic ?? false;
  const visitors = activity?.visitors ?? 0;
  const inWhopPlacement = placements.find((p) => p.kind === 'in_whop');
  const experienceId = useInWhopExperience(experimentId, inWhopPlacement, attach);
  const appAdded = Boolean(inWhopPlacement && experienceId);
  const linkSpots = placements.filter((p) => p.kind === 'link');
  const partnerSpots = placements.filter((p) => p.kind === 'partner');
  const hasLinks = linkSpots.length + partnerSpots.length > 0;
  // An in_whop placement is labelled with its free product's name.
  const freeProductName = freeProductNameProp || inWhopPlacement?.label;

  useEffect(() => {
    if (arrived) return;
    let cancelled = false;
    const load = async () => {
      try {
        const data = await api.getVisitActivity(experimentId);
        if (!cancelled) setActivity(data);
      } catch (err) {
        console.error('Failed to load visit activity:', err);
      }
    };
    load();
    const handle = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [experimentId, arrived]);

  const labels = placements.filter((p) => p.link).map(spotName);
  const theLinks = labels.length === 1 ? 'this link' : 'these links';
  const where = linkSpots.length === 1 ? spotName(linkSpots[0]) : 'each spot';
  const onePartner = partnerSpots.length === 1;
  const steps = {
    created: !hasLinks ? 'Test is live' : labels.length === 1 ? 'Link created' : 'Links created',
    visit: hasLinks ? 'Someone other than you visits' : 'First member visit',
  };
  // "I've posted it" covers the links; both kinds on one test share the tick.
  const postedRow =
    linkSpots.length > 0
      ? { label: `Link posted in ${where}`, action: "I've posted it" }
      : partnerSpots.length > 0
        ? {
            label: onePartner ? `Link sent to ${spotName(partnerSpots[0])}` : 'Links sent to your partners',
            action: onePartner ? "I've sent it" : "I've sent them",
          }
        : null;
  const explanation = [
    hasLinks ? `Everyone who opens ${theLinks} is split between your prices, and every sale is tracked.` : null,
    inWhopPlacement ? 'Free members who open the Upgrade tab are split between your prices.' : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <div>
        <Heading size="5" style={{ marginBottom: 'var(--space-2)' }}>
          {arrived
            ? 'People are finding your test'
            : visitors > 0
              ? `You're live — ${pluralize(visitors, 'visitor')} so far`
              : "You're live — waiting for the first visit"}
        </Heading>
        <Text size="2" color="gray">
          {explanation}
        </Text>
      </div>

      <PlacementLinks placements={placements} manage={manage} />
      {inWhopPlacement && !appAdded && (
        <AddAppInstructions freeProductName={freeProductName ?? ''} />
      )}

      <Card size="2">
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <CheckRow done>{steps.created}</CheckRow>
          {inWhopPlacement && (
            <li style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--space-2)', justifyContent: 'space-between' }}>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                <CheckRow done={appAdded}>{`App added to ${freeProductName || 'your free product'}`}</CheckRow>
              </ul>
              {!appAdded && <Spinner size="1" aria-label="Checking your free product" />}
            </li>
          )}
          {postedRow && (
            <li style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--space-2)', justifyContent: 'space-between' }}>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                <CheckRow done={posted || arrived}>{postedRow.label}</CheckRow>
              </ul>
              {!posted && !arrived && (
                <Button
                  type="button"
                  size="1"
                  variant="soft"
                  onClick={() => {
                    setPosted(true);
                    savePosted(experimentId);
                  }}
                >
                  {postedRow.action}
                </Button>
              )}
            </li>
          )}
          <li style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, flex: 1 }}>
              <CheckRow done={arrived}>
                {arrived && visitors > 0
                  ? `${steps.visit} — ${pluralize(visitors, 'visitor')} so far`
                  : steps.visit}
              </CheckRow>
            </ul>
            {!arrived && <Spinner size="1" aria-label="Checking for visits" />}
          </li>
        </ul>
        <Text size="1" color="gray" style={{ display: 'block', marginTop: 'var(--space-3)' }}>
          {[
            activity && activity.ownVisits > 0
              ? `We saw ${pluralize(activity.ownVisits, 'visit')} from your Whop account. Those don't count.`
              : null,
            hasLinks
              ? "If you open your own link in a browser, it counts as a visit: we can't tell it's you."
              : null,
            arrived
              ? null
              : hasLinks
                ? `This ticks at a second visitor or a sale. We check every ${POLL_MS / 1000} seconds.`
                : `We check every ${POLL_MS / 1000} seconds.`,
          ]
            .filter(Boolean)
            .join(' ')}
        </Text>
      </Card>
    </div>
  );
}
